import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const useListPosition = (expandOnTop?: boolean) => {
  const [mode, setMode] = useState<'hide' | 'init' | 'view'>('hide');
  const [listPosition, setListPosition] = useState<{
    marginLeft: number;
    marginTop: number;
    width: number;
  } | null>(null);
  const btnRef = useRef<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);

  const isView = useMemo(() => mode === 'view', [mode]);

  const toHide = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMode('hide');
  }, []);

  const onBtnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setListPosition({
          marginLeft: rect.left,
          marginTop: expandOnTop ? rect.top - 5 : rect.top + rect.height + 5,
          width: rect.width,
        });
        setMode('init');
      }
    },
    [expandOnTop],
  );

  useEffect(() => {
    if (!portalRef.current || !btnRef.current || mode === 'hide') {
      return;
    }

    const observer = new ResizeObserver(() => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setListPosition({
          marginLeft: rect.left,
          marginTop: expandOnTop ? rect.top - 5 : rect.top + rect.height + 5,
          width: rect.width,
        });
      }
    });

    observer.observe(portalRef.current);

    return () => {
      observer.disconnect();
    };
  }, [mode, expandOnTop]);

  useEffect(() => {
    if (mode === 'init') {
      setTimeout(() => {
        setMode('view');
      }, 20);
    }

    return () => {
      if (mode === 'view') {
        setTimeout(() => {
          setListPosition(null);
        }, 200);
      }
    };
  }, [mode]);

  return { isView, toHide, btnRef, portalRef, onBtnClick, listPosition };
};

export default useListPosition;
